import React, {forwardRef, useContext, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';

import styles from 'components/ProfileEdit/InterviewEditTab/InterviewTab/InterviewTab.module.scss';
import {convertToOptions, convertToValue} from 'helpers/SelectUtils';
import cn from 'classnames';
import Select from 'components/common/Form/Select';
import SimpleButton from 'components/common/Form/SimpleButton';
import {UserContext} from 'context/UserContext';
import getUserAvatarURL from 'components/common/getUserAvatarURL';
import ToggleButton from 'components/common/Form/ToggleButton';
import {TRANSLATE_RELATIONSHIP_STATUSES} from 'constants/RelationshipStatuses';
import TinyMCEEditor from 'components/common/TinyMCEEditor/TinyMCEEditor';
import {isCompanyAdmin, isRespondent} from 'constants/UserRoles';

const MiscellaneousInfo = ({id, values, errors, onChangeField, containerRef, isModal}) => {
    const {user, setAvatar} = useContext(UserContext);
    const avatarRef = useRef('');
    const {t} = useTranslation();
    const {about, relationshipStatus, availableForInterviews} = values;
    const handleAvatarChange = async () => {
        try {
            setAvatar(avatarRef.current.files[0]);
        } catch (err) {
            console.log(err);
        }
    };
    const avatarUrl = getUserAvatarURL(user);
    return (
        <div id={id} ref={containerRef} className={styles.wrapper}>
            <div className={cn({[styles.grid]: !isModal})}>
                <div className={styles.col}>
                    {!isModal && <span className={styles.title}>{t(`profile:text.miscellaneous`)}</span>}
                </div>
                <div className={cn(styles.col, styles.topMargin, {[styles.modal]: isModal})}>
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <div className={styles.avatar}>
                                <div className={styles.photo} style={{backgroundImage: `url(${avatarUrl})`}} />
                                <SimpleButton
                                    className={styles.changePhotoBtn}
                                    onClick={() => avatarRef.current.click()}
                                >
                                    <input
                                        type='file'
                                        ref={avatarRef}
                                        style={{display: 'none'}}
                                        onChange={handleAvatarChange}
                                    />
                                    {t('profile:text.change')}
                                </SimpleButton>
                            </div>
                        </div>
                        {!isModal && !isCompanyAdmin(user.role) && (
                            <div className={styles.col}>
                                <ToggleButton
                                    name='availableForInterviews'
                                    label={t(`profile:text.availableForInterviews`)}
                                    toggled={availableForInterviews}
                                    onChangeField={onChangeField}
                                    disabled={!user.welcomeFilled}
                                    tooltipText={!user.welcomeFilled && t('profile:text.completeProfile')}
                                />
                            </div>
                        )}
                    </div>
                    {isRespondent(user.role) && (
                        <div className={cn(styles.row)}>
                            <Select
                                fullWidth
                                name='relationshipStatus'
                                label={t(`profile:text.relationshipStatusInfoLabel`)}
                                placeholder={t(`profile:text.selectYourRelationshipStatus`)}
                                options={convertToOptions(TRANSLATE_RELATIONSHIP_STATUSES(t))}
                                value={convertToValue(relationshipStatus, TRANSLATE_RELATIONSHIP_STATUSES(t))}
                                error={t(errors.relationshipStatus)}
                                onChangeField={onChangeField}
                                className={styles.withWidth}
                            />
                        </div>
                    )}
                    <div className={cn(styles.row, styles.topMargin)}>
                        <TinyMCEEditor
                            initial={about}
                            onChangeField={onChangeField}
                            error={t(errors.about)}
                            name='about'
                            charLimit={500}
                            classes={{wrapper: styles.withWidth}}
                            label={t('profile:text.aboutMe')}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

MiscellaneousInfo.propTypes = {
    id: PropTypes.string,
    values: PropTypes.object,
    errors: PropTypes.object,
    onChangeField: PropTypes.func,
};

MiscellaneousInfo.defaultProps = {
    id: null,
    values: {},
    errors: {},
    onChangeField: f => f,
};

export default forwardRef((props, ref) => <MiscellaneousInfo {...props} containerRef={ref} />);
